/**
 * Stylist workspace chrome, built to the same structure and spacing as the
 * management console's AppShell.
 */

import { useState } from 'react';
import { LogOut01, Menu02, Moon01, Sun, XClose } from '@untitledui/icons';
import { NavLink, Outlet, useMatch, useNavigate } from 'react-router';
import { Badge } from '@ui/components/base/badges/badges';
import { Button } from '@ui/components/base/buttons/button';
import { useTheme } from '@ui/providers/theme-provider';
import { cx } from '@ui/utils/cx';
import { signOut } from './auth';

const NAVIGATION = [
  {
    label: 'Workspace',
    items: [
      { to: '/', label: 'My members', end: true },
      { to: '/review', label: "Tomorrow's outfits", end: false },
    ],
  },
] as const;

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const dark = theme === 'dark';
  return (
    <Button
      size="sm"
      color="tertiary"
      aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
      iconLeading={dark ? Sun : Moon01}
      onClick={() => setTheme(dark ? 'light' : 'dark')}
    />
  );
}

export function Shell() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const exit = () => {
    signOut();
    void navigate('/sign-in', { replace: true });
  };
  // An outfit under review lays the combination beside the member, so it gets
  // the whole viewport rather than the reading width the other pages keep.
  const fullWidth = useMatch('/review/:outfitId') !== null;

  return (
    <div className="flex min-h-dvh flex-col bg-secondary">
      <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center justify-between gap-3 border-b border-secondary bg-primary px-4 md:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <Button
            className="lg:hidden"
            size="sm"
            color="tertiary"
            aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={menuOpen}
            aria-controls="stylist-navigation"
            iconLeading={menuOpen ? XClose : Menu02}
            onClick={() => setMenuOpen((open) => !open)}
          />
          <a href="/" className="flex min-w-0 items-center gap-2.5">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand-solid text-sm font-bold text-white">
              CT
            </span>
            <span className="flex min-w-0 flex-col leading-tight">
              <strong className="truncate text-sm font-semibold text-primary">Closet Theory</strong>
              <span className="truncate text-xs text-tertiary">Stylist workspace</span>
            </span>
          </a>
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <div className="hidden min-w-0 flex-col items-end sm:flex">
            <span className="truncate text-sm font-medium text-primary">Stylist</span>
            <Badge type="pill-color" size="sm" color="blue">
              {import.meta.env.MODE}
            </Badge>
          </div>
          <Button size="sm" color="tertiary" iconLeading={LogOut01} onClick={exit}>
            Sign out
          </Button>
        </div>
      </header>

      <div className="flex flex-1 items-stretch">
        <nav
          id="stylist-navigation"
          aria-label="Stylist primary navigation"
          className={cx(
            'fixed inset-x-0 top-16 bottom-0 z-20 w-full overflow-y-auto border-r border-secondary bg-primary px-3 py-4 lg:sticky lg:top-16 lg:block lg:h-[calc(100dvh-4rem)] lg:w-64 lg:shrink-0',
            menuOpen ? 'block' : 'hidden',
          )}
        >
          <div className="flex flex-col gap-5">
            {NAVIGATION.map((group) => (
              <div key={group.label} className="flex flex-col gap-1">
                <h2 className="px-3 pb-1 text-xs font-semibold text-quaternary uppercase">
                  {group.label}
                </h2>
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      cx(
                        'rounded-lg px-3 py-2 text-sm font-medium outline-brand focus-visible:outline-2',
                        isActive
                          ? 'bg-active text-primary'
                          : 'text-tertiary hover:bg-primary_hover hover:text-secondary',
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            ))}
          </div>
        </nav>

        <main
          className={cx('min-w-0 flex-1 px-4 py-6', fullWidth ? 'md:px-6' : 'md:px-8')}
          id="main-content"
          aria-live="polite"
        >
          <div
            className={cx('mx-auto flex flex-col gap-5', fullWidth ? 'w-full' : 'max-w-[1600px]')}
          >
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
